import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchableDropdown from '../components/SearchableDropdown';
import TaskAssigneePicker from '../components/TaskAssigneePicker';
import TaskEventProjectFields from '../components/TaskEventProjectFields';
import TagSelector from '../components/TagSelector';
import { useAuth } from '../context/AuthContext';
import { createTask, createTaskSubtask, type TaskRow } from '../api/tasks';
import { fetchProjectsCategorized, type ProjectRow } from '../api/projects';
import { fetchEvents, type EventRow } from '../api/events';
import { mergeEventChoices } from '../utils/taskEventProject';
import { PROJECT_BOARD_FIRST_COLUMN_ID } from '../constants/projectBoard';
import { loadTasksForDependencyPicker } from '../utils/taskDependencyPicker';
import { fetchVerifiedUsers, ROLE_OPTIONS } from '../api/users';
import { fetchClients } from '../api/clients';
import { TAG_PILL_COLORS, ROLE_LABELS } from '../constants/taskTags';
import './AddTask.css';
import './ClientDetail.css';
import './TaskDetail.css';

interface AddTaskFormProps {
  onClose: () => void;
  /** When set, new tasks are scoped to this project board column. */
  projectBoardContext?: { projectId: number; boardColumnId: string; eventId?: number };
  /** If provided, task is not navigated to; callback receives the created task (e.g. project board). */
  onCreated?: (task: TaskRow) => void;
}

interface DraftSubtask {
  /** Local-only key for list rendering. */
  key: string;
  title: string;
  assigneeUserId: number | null;
}

let draftSubtaskCounter = 0;
const newDraftSubtask = (): DraftSubtask => ({
  key: `draft-${++draftSubtaskCounter}`,
  title: '',
  assigneeUserId: null,
});

const AddTaskForm: React.FC<AddTaskFormProps> = ({ onClose, projectBoardContext, onCreated }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [verifiedUsers, setVerifiedUsers] = useState<{ id: number; username: string }[]>([]);
  const [clientOptions, setClientOptions] = useState<{ id: number; company: string }[]>([]);

  const [name, setName] = useState('');
  const [status, setStatus] = useState<string>('Unassigned');
  const [assignedToUserIds, setAssignedToUserIds] = useState<number[]>([]);
  const [clientId, setClientId] = useState<number | null>(null);
  const [clientDisplay, setClientDisplay] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [subtasks, setSubtasks] = useState<DraftSubtask[]>([]);
  const [priority, setPriority] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [dependencyOptions, setDependencyOptions] = useState<TaskRow[]>([]);
  const [dependsOnTaskId, setDependsOnTaskId] = useState<number | null>(null);
  const [projectOptions, setProjectOptions] = useState<ProjectRow[]>([]);
  const [eventOptions, setEventOptions] = useState<EventRow[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    () => projectBoardContext?.projectId ?? null
  );
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  const effectiveProjectIdForDeps = selectedProjectId;

  const sortedProjectChoices = useMemo(
    () =>
      [...projectOptions].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      ),
    [projectOptions]
  );

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchVerifiedUsers(),
      user?.id ? fetchClients('client', user.id) : fetchClients('client'),
    ])
      .then(([users, clients]) => {
        if (cancelled) return;
        setVerifiedUsers(users.map((u) => ({ id: u.id, username: u.username })));
        setClientOptions(clients.map((c) => ({ id: c.id, company: c.company })));
      })
      .catch(() => {
        if (!cancelled) {
          setVerifiedUsers([]);
          setClientOptions([]);
        }
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setProjectOptions([]);
      return;
    }
    fetchProjectsCategorized(user.id)
      .then((data) => {
        if (cancelled) return;
        const isAdmin = Array.isArray(user.roles) && user.roles.includes('admin');
        const list =
          isAdmin && Array.isArray(data.all) && data.all.length > 0
            ? data.all
            : data.my ?? [];
        setProjectOptions(list);
      })
      .catch(() => {
        if (!cancelled) setProjectOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.roles]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setEventOptions([]);
      return;
    }
    fetchEvents(user.id)
      .then((data) => {
        if (cancelled) return;
        const list = mergeEventChoices(data);
        setEventOptions(list);
        if (projectBoardContext?.eventId != null) {
          setSelectedEventId(projectBoardContext.eventId);
        }
      })
      .catch(() => {
        if (!cancelled) setEventOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, projectBoardContext?.eventId]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setDependencyOptions([]);
      return;
    }
    const pid = effectiveProjectIdForDeps;
    loadTasksForDependencyPicker(user.id, pid, null)
      .then((list) => {
        if (!cancelled) setDependencyOptions(list);
      })
      .catch(() => {
        if (!cancelled) setDependencyOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, effectiveProjectIdForDeps]);

  const toggleTag = (role: string) => {
    setTags((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  /** Only people assigned to the task can be assigned to a subtask (enforced by the backend). */
  const subtaskAssigneeOptions = useMemo(
    () =>
      assignedToUserIds
        .map((uid) => verifiedUsers.find((u) => u.id === uid))
        .filter((u): u is { id: number; username: string } => !!u),
    [assignedToUserIds, verifiedUsers]
  );

  useEffect(() => {
    const assignedSet = new Set(assignedToUserIds);
    setSubtasks((prev) => {
      let changed = false;
      const next = prev.map((s) => {
        if (s.assigneeUserId != null && !assignedSet.has(s.assigneeUserId)) {
          changed = true;
          return { ...s, assigneeUserId: null };
        }
        return s;
      });
      return changed ? next : prev;
    });
  }, [assignedToUserIds]);

  const addSubtaskRow = () => setSubtasks((prev) => [...prev, newDraftSubtask()]);
  const removeSubtaskRow = (key: string) =>
    setSubtasks((prev) => prev.filter((s) => s.key !== key));
  const updateSubtaskRow = (key: string, patch: Partial<DraftSubtask>) =>
    setSubtasks((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Task name is required.');
      return;
    }
    if (tags.length === 0) {
      setError('Select at least one role tag.');
      return;
    }
    setSubmitting(true);
    try {
      const needsBoardColumn = selectedProjectId != null || selectedEventId != null;
      const boardColumn =
        projectBoardContext &&
        ((projectBoardContext.eventId != null && projectBoardContext.eventId === selectedEventId) ||
          (selectedProjectId != null && selectedProjectId === projectBoardContext.projectId))
          ? projectBoardContext.boardColumnId
          : PROJECT_BOARD_FIRST_COLUMN_ID;

      const task = await createTask({
        name: name.trim(),
        status,
        assigned_to_user_ids: assignedToUserIds.length > 0 ? assignedToUserIds : undefined,
        assigned_by_user_id: user?.id ?? undefined,
        client_id: clientId ?? undefined,
        tags,
        start_date: startDate.trim() || undefined,
        due_date: dueDate.trim() || undefined,
        description: description.trim() || undefined,
        priority: priority.trim() || undefined,
        ...(selectedProjectId != null ? { project_id: selectedProjectId } : {}),
        ...(selectedEventId != null ? { event_id: selectedEventId } : {}),
        ...(needsBoardColumn ? { project_board_column_id: boardColumn } : {}),
        ...(dependsOnTaskId != null ? { depends_on_task_id: dependsOnTaskId } : {}),
      });

      const validSubtasks = subtasks
        .map((s) => ({ ...s, title: s.title.trim() }))
        .filter((s) => s.title !== '');
      if (validSubtasks.length > 0 && user?.id != null) {
        const assignedSet = new Set(assignedToUserIds);
        for (let i = 0; i < validSubtasks.length; i++) {
          const s = validSubtasks[i];
          const assignee =
            s.assigneeUserId != null && assignedSet.has(s.assigneeUserId) ? s.assigneeUserId : null;
          try {
            await createTaskSubtask(task.id, {
              user_id: user.id,
              title: s.title,
              assignee_user_id: assignee,
              sort_order: i,
            });
          } catch {
            // Don't fail the whole task creation if a single subtask can't be created.
          }
        }
      }

      if (onCreated) {
        onCreated(task);
        onClose();
        return;
      }
      navigate(`/tasks/${task.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create task.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-body">
      <form className="add-task-form task-detail-cards" onSubmit={handleSubmit}>
        <div className="form-section task-detail-card">
          <h3 className="section-title">Task</h3>
          <div className="form-group">
            <label htmlFor="task-name" className="form-label">Name *</label>
            <input
              id="task-name"
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g. Prepare quarterly report"
            />
          </div>
          <div className="form-group">
            <label htmlFor="task-status" className="form-label">Status</label>
            <select
              id="task-status"
              className="form-select"
              value={status}
              onChange={(e) => {
                const newStatus = e.target.value;
                setStatus(newStatus);
                if (newStatus === 'Unassigned') {
                  setAssignedToUserIds([]);
                }
              }}
            >
              <option value="Unassigned">Unassigned</option>
              <option value="To-Do">To-Do</option>
              <option value="In Progress">In Progress</option>
              <option value="On Hold">On Hold</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
        </div>

        <div className="form-section task-detail-card">
          <h3 className="section-title">Assignment &amp; company</h3>
          <div className="form-group">
            <label className="form-label">Assign to</label>
            <p className="page-subtitle" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
              Add one or more people. Leave empty for an unassigned task.
            </p>
            <TaskAssigneePicker
              users={verifiedUsers}
              value={assignedToUserIds}
              onChange={(ids) => {
                setAssignedToUserIds(ids);
                if (ids.length > 0 && status === 'Unassigned') {
                  setStatus('To-Do');
                } else if (ids.length === 0) {
                  setStatus('Unassigned');
                }
              }}
              addPlaceholder="Search user to add…"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Company (client)</label>
            <SearchableDropdown
              options={clientOptions.map((c) => c.company)}
              value={clientDisplay}
              onChange={(display) => {
                setClientDisplay(display);
                const c = clientOptions.find((x) => x.company === display);
                setClientId(c ? c.id : null);
              }}
              placeholder="Select company (optional)"
            />
          </div>
          <TaskEventProjectFields
            events={eventOptions}
            projects={sortedProjectChoices}
            eventId={selectedEventId}
            projectId={selectedProjectId}
            onEventIdChange={setSelectedEventId}
            onProjectIdChange={setSelectedProjectId}
            onAssociationChange={() => setDependsOnTaskId(null)}
            eventSelectId="add-task-event"
            projectSelectId="add-task-project"
            disabled={submitting}
          />
        </div>

        <div className="form-section task-detail-card">
          <h3 className="section-title">Description</h3>
          <div className="form-group">
            <label htmlFor="task-description" className="form-label">What is this task about?</label>
            <textarea
              id="task-description"
              className="form-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Context, scope, or instructions…"
            />
          </div>
        </div>

        <div className="form-section task-detail-card">
          <h3 className="section-title">
            Subtasks <span className="add-task-optional-label">(optional)</span>
          </h3>
          <p className="page-subtitle" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
            Break the task into steps. You can assign each subtask to one of the people added in
            &ldquo;Assign to&rdquo; above.
          </p>
          {subtasks.length > 0 && (
            <div className="add-task-subtasks-list">
              {subtasks.map((s) => (
                <div className="add-task-subtask-row" key={s.key}>
                  <input
                    type="text"
                    className="form-input add-task-subtask-title"
                    placeholder="Subtask title…"
                    value={s.title}
                    maxLength={500}
                    onChange={(e) => updateSubtaskRow(s.key, { title: e.target.value })}
                    aria-label="Subtask title"
                  />
                  <select
                    className="form-select add-task-subtask-assignee"
                    value={s.assigneeUserId != null ? String(s.assigneeUserId) : ''}
                    onChange={(e) =>
                      updateSubtaskRow(s.key, {
                        assigneeUserId: e.target.value === '' ? null : parseInt(e.target.value, 10),
                      })
                    }
                    disabled={subtaskAssigneeOptions.length === 0}
                    aria-label="Subtask assignee"
                    title={
                      subtaskAssigneeOptions.length === 0
                        ? 'Add people to “Assign to” first'
                        : undefined
                    }
                  >
                    <option value="">Unassigned</option>
                    {subtaskAssigneeOptions.map((u) => (
                      <option key={u.id} value={String(u.id)}>
                        {u.username}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="add-task-subtask-remove"
                    onClick={() => removeSubtaskRow(s.key)}
                    aria-label="Remove subtask"
                    title="Remove subtask"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button type="button" className="add-task-subtask-add-btn" onClick={addSubtaskRow}>
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.4rem' }} aria-hidden />
            Add subtask
          </button>
        </div>

        <div className="form-section task-detail-card">
          <h3 className="section-title">Role tags *</h3>
          <p className="page-subtitle" style={{ marginBottom: '0.75rem' }}>
            At least one tag required.
          </p>
          <TagSelector
            options={ROLE_OPTIONS}
            labels={ROLE_LABELS}
            selected={tags}
            onChange={setTags}
            colors={TAG_PILL_COLORS}
          />
        </div>

        <div className="form-section task-detail-card">
          <h3 className="section-title">Dates &amp; Priority</h3>
          <div className="form-group">
            <label htmlFor="task-start" className="form-label">Start Date</label>
            <input
              id="task-start"
              type="date"
              className="form-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="task-due" className="form-label">Due Date</label>
            <input
              id="task-due"
              type="date"
              className="form-input"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="task-priority" className="form-label">Priority</label>
            <select
              id="task-priority"
              className="form-select"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              <option value="">—</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Urgent">Urgent</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="task-depends-on" className="form-label">
              Depends on
            </label>
            <select
              id="task-depends-on"
              className="form-select"
              value={dependsOnTaskId === null ? '' : String(dependsOnTaskId)}
              onChange={(e) => {
                const v = e.target.value;
                setDependsOnTaskId(v === '' ? null : parseInt(v, 10));
              }}
            >
              <option value="">— None —</option>
              {dependencyOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} (#{t.id})
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <p className="create-order-error" role="alert">{error}</p>}

        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="update-button" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create Task'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddTaskForm;
